/**
 * PayoffChartPro — polished P&L-at-expiry payoff for the basket review dialog.
 * Shaded profit (green) / loss (red) zones, prominent current-spot marker,
 * labelled break-even lines, max-profit/loss guides, and a dashed "today" curve.
 * Kept separate from the FNO PayoffCurve so the Options Lab is unaffected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceDot, ReferenceArea, ResponsiveContainer, Customized,
  usePlotArea, useXAxisDomain,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { optionsService } from '../../services/options.service';

interface Props {
  basket: any;        // OptionBasket entity (payoffData, legs, maxProfit, maxLoss, breakEvens)
  spotPrice: number;
  /** Profitable spot range(s) [lo, hi] — drawn as a highlighted vertical band so
   *  the user sees at a glance where the position makes money at expiry. */
  profitBand?: Array<[number, number]> | null;
  /** Underlying (base, e.g. 'BTC') — when set, the live Delta Call/Put OI-by-strike is
   *  drawn as a histogram at the base of the payoff so it's clear where OI is building. */
  underlyingSymbol?: string;
  /** Hide the built-in header (Max profit/loss/break-even + legs line) so the caller can
   *  render its own single, consistent summary (avoids a duplicate "Max Profit"). */
  hideHeader?: boolean;
}

const PayoffChartPro: React.FC<Props> = ({ basket, spotPrice, profitBand, underlyingSymbol, hideHeader }) => {
  const { C } = useAppTheme();
  const pd = basket?.payoffData;

  // Delta OI-by-strike (CE resistance / PE support) for the underlying — drawn under
  // the payoff so the user sees where Call / Put OI is concentrated vs the payoff shape.
  const [oi, setOi] = useState<Array<{ strike: number; ceOi: number; peOi: number }>>([]);
  useEffect(() => {
    let cancel = false;
    if (!underlyingSymbol) { setOi([]); return; }
    optionsService.getOptionProfile(underlyingSymbol, 'D')
      .then((r: any) => {
        if (cancel) return;
        const strikes = r?.data?.data?.strikes || [];
        setOi(strikes
          .map((s: any) => ({ strike: Number(s.strike), ceOi: Number(s.ceOi) || 0, peOi: Number(s.peOi) || 0 }))
          .filter((s: any) => Number.isFinite(s.strike) && (s.ceOi > 0 || s.peOi > 0)));
      })
      .catch(() => { if (!cancel) setOi([]); });
    return () => { cancel = true; };
  }, [underlyingSymbol]);

  const data = useMemo(() => {
    if (!pd?.spotRange?.length) return [];
    return pd.spotRange.map((s: number, i: number) => {
      const pnl = Number(pd.expiry[i] ?? 0);
      return {
        spot: s,
        profit: pnl > 0 ? pnl : 0,
        loss: pnl < 0 ? pnl : 0,
        expiry: pnl,
        today: pd.today ? Number(pd.today[i] ?? 0) : null,
        far: pd.far ? Number(pd.far[i] ?? 0) : null,
      };
    });
  }, [pd]);

  // P&L at the current spot (interpolated) — drives the spot dot.
  const pnlAtSpot = useMemo(() => {
    if (!pd?.spotRange?.length || !spotPrice) return null;
    const xs = pd.spotRange as number[];
    let lo = 0;
    for (let i = 0; i < xs.length; i++) { if (xs[i] <= spotPrice) lo = i; }
    const hi = Math.min(lo + 1, xs.length - 1);
    const y0 = Number(pd.expiry[lo] ?? 0), y1 = Number(pd.expiry[hi] ?? 0);
    if (xs[hi] === xs[lo]) return y0;
    const t = (spotPrice - xs[lo]) / (xs[hi] - xs[lo]);
    return y0 + t * (y1 - y0);
  }, [pd, spotPrice]);

  if (!data.length) return null;

  const maxProfit = Number(basket.maxProfit || 0);
  const maxLoss = Math.abs(Number(basket.maxLoss || 0));
  const breakEvens: number[] = basket.breakEvens || [];
  const legs: any[] = basket.legs || [];
  const green = C.chartBull || '#26a69a';
  const red = C.chartBear || '#ef5350';

  // Clamp the x-axis (underlying price) to the ACTION zone — the leg strikes,
  // break-evens and current spot, plus a margin — so the flat max-profit/max-loss
  // tails don't compress the transition. (Spreads especially: most of the full
  // spotRange is flat.) Falls back to the full range if we can't find a window.
  const xDomain: [number, number] | undefined = (() => {
    const xs = (pd?.spotRange as number[]) || [];
    if (xs.length < 2) return undefined;
    const dMin = xs[0], dMax = xs[xs.length - 1];
    const focus = [...legs.map((l: any) => Number(l.strike)), ...breakEvens.map(Number), spotPrice]
      .filter(v => Number.isFinite(v) && v > 0);
    if (!focus.length) return [dMin, dMax];
    let lo = Math.min(...focus), hi = Math.max(...focus);
    const span = Math.max(hi - lo, (hi || 1) * 0.02);
    const margin = span * 0.6; // generous enough to still show the flat / sloping context
    lo -= margin; hi += margin;
    return [Math.max(dMin, lo), Math.min(dMax, hi)];
  })();

  // OI-by-strike histogram at the base of the plot. recharts v3 no longer passes
  // xAxisMap/offset to <Customized>, so this reads the plot rectangle + x-domain via the
  // v3 hooks (usePlotArea / useXAxisDomain) and builds the linear pixel mapping itself.
  // PE (support) green from the baseline, CE (resistance) red stacked on top.
  const OiHistogram: React.FC = () => {
    const plot = usePlotArea();
    const xDom = useXAxisDomain();
    if (!oi.length || !plot || !Array.isArray(xDom) || xDom.length < 2) return null;
    const dMin = Number(xDom[0]), dMax = Number(xDom[1]);
    if (!(dMax > dMin)) return null;
    const plotLeft = Number(plot.x), plotTop = Number(plot.y), plotW = Number(plot.width), plotH = Number(plot.height);
    if (!(plotW > 0) || !(plotH > 0)) return null;
    const xOf = (v: number) => plotLeft + ((v - dMin) / (dMax - dMin)) * plotW;
    const baseY = plotTop + plotH;
    const bandH = Math.max(28, plotH * 0.26);
    const vis = oi
      .map(p => ({ p, x: xOf(p.strike) }))
      .filter(r => Number.isFinite(r.x) && r.x >= plotLeft - 2 && r.x <= plotLeft + plotW + 2)
      .sort((a, b) => a.x - b.x);
    if (!vis.length) return null;
    let maxTot = 0; for (const r of vis) maxTot = Math.max(maxTot, r.p.ceOi + r.p.peOi);
    if (maxTot <= 0) return null;
    let minGap = Infinity; for (let i = 1; i < vis.length; i++) minGap = Math.min(minGap, vis[i].x - vis[i - 1].x);
    const bw = Math.max(3, Math.min(Number.isFinite(minGap) ? minGap * 0.62 : 8, 10));
    // √-scale (OI is heavily skewed) so smaller walls stay visible next to the max.
    const hOf = (v: number) => (v > 0 ? Math.max(2, Math.sqrt(v / maxTot) * bandH) : 0);
    return (
      <g>
        {vis.map((r, i) => {
          const peH = hOf(r.p.peOi);
          const ceH = hOf(r.p.ceOi);
          return (
            <g key={i}>
              {peH > 0 && <rect x={r.x - bw / 2} y={baseY - peH} width={bw} height={peH} fill={green} opacity={0.5} />}
              {ceH > 0 && <rect x={r.x - bw / 2} y={baseY - peH - ceH} width={bw} height={ceH} fill={red} opacity={0.5} />}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <Box>
      {!hideHeader && <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Payoff at expiry</Typography>
        <Typography variant="caption" sx={{ color: green, fontWeight: 700 }}>Max profit ₹{Math.round(maxProfit).toLocaleString('en-IN')}</Typography>
        <Typography variant="caption" sx={{ color: red, fontWeight: 700 }}>Max loss {maxLoss === 0 ? '∞ (undefined)' : `₹${Math.round(maxLoss).toLocaleString('en-IN')}`}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Break-even {breakEvens.length ? breakEvens.map(b => Math.round(b).toLocaleString('en-IN')).join(' / ') : '—'}
        </Typography>
      </Box>
      {/* Legs used to build this payoff, with the entry price of each option leg. */}
      {legs.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', mb: 0.4, lineHeight: 1.5 }}>
          {[...legs].sort((a, b) => (b.strike || 0) - (a.strike || 0)).map((l, i) => {
            const prem = l.ltp ?? l.premium ?? l.entryPremium ?? l.price;
            return `${l.action === 'BUY' ? '+' : '−'}${l.optionType || ''}${Math.round(Number(l.strike)).toLocaleString('en-IN')}${prem != null ? ` @ ${Number(prem).toFixed(2)}` : ''}`;
          }).join('   ')}
        </Typography>
      )}
      </>}
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id="ppPro-profit" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={green} stopOpacity={0.45} />
              <stop offset="100%" stopColor={green} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="ppPro-loss" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor={red} stopOpacity={0.45} />
              <stop offset="100%" stopColor={red} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.35} />
          <XAxis dataKey="spot" type="number" domain={xDomain || ['dataMin', 'dataMax']} allowDataOverflow
            tickFormatter={(v: number) => `${(v / 1000).toFixed(1)}K`} tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={(v: number) => `₹${Math.round(v)}`} tick={{ fontSize: 10 }} width={52} />
          <Tooltip
            // Vertical crosshair line that tracks the cursor; the tooltip header
            // shows the underlying price at that x-position.
            cursor={{ stroke: C.spot || '#ffb74d', strokeWidth: 1, strokeDasharray: '4 3' }}
            formatter={(v: any, name: string) => {
              const labels: Record<string, string> = { expiry: 'At near expiry', today: 'Today', far: 'At far expiry' };
              return labels[name] ? [`₹${Number(v).toFixed(0)}`, labels[name]] : [null, null];
            }}
            labelFormatter={(l: any) => `Underlying ₹${Number(l).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}`, fontSize: 12 }}
            labelStyle={{ fontWeight: 800 }}
          />
          {/* Operative profitable spot range(s) — vertical highlight band */}
          {(profitBand || []).map(([lo, hi], i) => (
            Number.isFinite(lo) && Number.isFinite(hi) && hi > lo
              ? <ReferenceArea key={`pb${i}`} x1={lo} x2={hi} fill={green} fillOpacity={0.08} stroke={green} strokeOpacity={0.25} strokeDasharray="3 3" />
              : null
          ))}
          {/* Profit / loss shaded zones (split at zero) */}
          <Area type="monotone" dataKey="profit" stroke="none" fill="url(#ppPro-profit)" baseValue={0} isAnimationActive={false} />
          <Area type="monotone" dataKey="loss" stroke="none" fill="url(#ppPro-loss)" baseValue={0} isAnimationActive={false} />
          <ReferenceLine y={0} stroke={C.chartGrid} strokeWidth={1.5} />
          {/* Leg strikes — faint guides */}
          {legs.map((leg, i) => (
            <ReferenceLine key={`k${i}`} x={leg.strike} stroke={C.chartGrid} strokeDasharray="2 4" opacity={0.5} />
          ))}
          {/* Break-evens — labelled with their underlying VALUE on the x-axis */}
          {breakEvens.map((be, i) => (
            <ReferenceLine key={`be${i}`} x={be} stroke={C.accent || '#ab47bc'} strokeDasharray="5 3"
              label={{ value: `BE ${Math.round(be).toLocaleString('en-IN')}`, fill: C.accent || '#ab47bc', fontSize: 10, position: 'insideBottom', fontWeight: 700 }} />
          ))}
          {/* Current spot — prominent */}
          {spotPrice > 0 && (
            <ReferenceLine x={spotPrice} stroke={C.spot || '#ffb74d'} strokeWidth={2}
              label={{ value: `Spot ${Math.round(spotPrice).toLocaleString('en-IN')}`, fill: C.spot || '#ffb74d', fontSize: 11, position: 'insideTopRight', fontWeight: 700 }} />
          )}
          {pnlAtSpot != null && spotPrice > 0 && (
            <ReferenceDot x={spotPrice} y={pnlAtSpot} r={4} fill={C.spot || '#ffb74d'} stroke="#000" strokeWidth={0.5} />
          )}
          <Line type="monotone" dataKey="expiry" stroke={green} strokeWidth={2.5} dot={false} name="expiry" />
          {pd.today && <Line type="monotone" dataKey="today" stroke={C.chartLine1 || '#42a5f5'} strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="today" />}
          {pd.far && <Line type="monotone" dataKey="far" stroke={C.accent || '#ab47bc'} strokeWidth={1.5} strokeDasharray="3 4" dot={false} name="far" />}
          {/* Delta OI-by-strike histogram at the base — drawn last so it's clearly visible. */}
          {oi.length > 0 && <Customized component={OiHistogram} />}
        </ComposedChart>
      </ResponsiveContainer>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 0.3, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: green }}>— {pd.far ? 'Near expiry' : 'At expiry'}</Typography>
        <Typography variant="caption" sx={{ color: C.chartLine1 || '#42a5f5' }}>--- Today</Typography>
        {pd.far && <Typography variant="caption" sx={{ color: C.accent || '#ab47bc' }}>··· Far expiry (wings intrinsic)</Typography>}
        {oi.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            OI by strike: <span style={{ color: red, fontWeight: 700 }}>▮ CE (resistance)</span> · <span style={{ color: green, fontWeight: 700 }}>▮ PE (support)</span>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PayoffChartPro;
